import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense, useEffect, useState } from "react";
import CookieConsent from "@/components/CookieConsent";
import StructuredData from "@/components/StructuredData";

import Index from "./pages/Index";

// Lazy load non-critical pages
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const AGB = lazy(() => import("./pages/AGB"));
const UeberUns = lazy(() => import("./pages/UeberUns"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const Referenzen = lazy(() => import("./pages/Referenzen"));
const Termin = lazy(() => import("./pages/Termin"));
const Klassisch = lazy(() => import("./pages/leistungen/Klassisch"));
const KIProduktionen = lazy(() => import("./pages/leistungen/KIProduktionen"));


const Danke = lazy(() => import("./pages/Danke"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const KundenOnboarding = lazy(() => import("./pages/KundenOnboarding"));
const WebshopOnboarding = lazy(() => import("./pages/WebshopOnboarding"));
const Admin = lazy(() => import("./pages/Admin"));
const Preiskalkulator = lazy(() => import("./pages/Preiskalkulator"));
const Angebot = lazy(() => import("./pages/Angebot"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Deferred Meta Pixel loader
const DeferredMetaPixel = () => {
  const [shouldLoad, setShouldLoad] = useState(false);
  
  useEffect(() => {
    // Load Meta Pixel after initial render is complete
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => setShouldLoad(true), { timeout: 3000 });
    } else {
      setTimeout(() => setShouldLoad(true), 2000);
    }
  }, []);
  
  if (!shouldLoad) return null;
  
  // Dynamically import MetaPixel
  const MetaPixel = lazy(() => import("@/components/MetaPixel"));
  return (
    <Suspense fallback={null}>
      <MetaPixel />
    </Suspense>
  );
};

const queryClient = new QueryClient();

// Minimal loading fallback
const PageFallback = () => <div className="min-h-screen bg-background" />;

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <CookieConsent />
        <DeferredMetaPixel />
        <StructuredData />
        <BrowserRouter>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/datenschutz" element={<Datenschutz />} />
                <Route path="/agb" element={<AGB />} />
                <Route path="/ueber-uns" element={<UeberUns />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/referenzen" element={<Referenzen />} />
                <Route path="/termin" element={<Termin />} />
                <Route path="/leistungen/klassisch" element={<Klassisch />} />
                <Route path="/leistungen/ki-produktionen" element={<KIProduktionen />} />


                <Route path="/danke" element={<Danke />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/kunden-onboarding" element={<KundenOnboarding />} />
                <Route path="/webshop-onboarding" element={<WebshopOnboarding />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/preiskalkulator" element={<Preiskalkulator />} />
                <Route path="/angebot/:id" element={<Angebot />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
